import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AmplifierData,
  ProgramStatusMessage,
  ProgramStatusResponseMessage,
  HamlibControlRequestMessage,
} from './amplifier';
import { useI18n } from '../../i18n/useI18n';
import { amplifierMqttService } from './AmplifierMqttService';

/** 功放中间区域：频率/模式/频段选择、输入输出功率、电压电流温度 */
interface TopBarMiddleProps {
  deviceId?: string;
  amplifierData?: AmplifierData | null;
  programStatusMessage?: ProgramStatusMessage | null;
  hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
}

/** 波形条采样点数量（横向滚动，保留最近 N 个点） */
const WAVEFORM_SAMPLE_SIZE = 50;

/** 将采样点数组转为 SVG path d，用于波形条绘制（viewBox 0 0 100 10，左旧右新） */
function samplesToPath(samples: number[], viewWidth = 100, viewHeight = 10): string {
  if (samples.length === 0) return `M0,${viewHeight / 2} L${viewWidth},${viewHeight / 2}`;
  const min = Math.min(...samples);
  const max = Math.max(...samples);
  const range = max - min || 1;
  const padding = 1;
  const usableHeight = viewHeight - 2 * padding;
  const points = samples.map((v, i) => {
    const x = samples.length === 1 ? viewWidth / 2 : (i / (samples.length - 1)) * viewWidth;
    const y = padding + (1 - (v - min) / range) * usableHeight;
    return `${x},${y}`;
  });
  return `M${points.join(' L')}`;
}

const TopBarMiddleInner: React.FC<TopBarMiddleProps> = ({ deviceId: propDeviceId, amplifierData, programStatusMessage, hamlibControlRequestMessage }) => {
    const location = useLocation();
    const { t } = useI18n();

    // ---------- 派生数据（只读用 Message，下发给设备用 Response 深拷贝） ----------
    /** 仅用于渲染，不修改 */
    const programStatusMessageData = programStatusMessage?.data;
    /** 深拷贝，用于组包下发；不参与渲染 */
    const programStatusResponseMessage: ProgramStatusResponseMessage | null = programStatusMessage
        ? {
            msg_id: programStatusMessage.msg_id,
            type: 'response',
            cmd: 'update_program_status',
            timestamp: programStatusMessage.timestamp,
            data: {
                ...programStatusMessage.data,
                freq_band_name: programStatusMessage.data.freq_band_name ? [...programStatusMessage.data.freq_band_name] : [],
                freq_band: programStatusMessage.data.freq_band ? [...programStatusMessage.data.freq_band] : [],
                band_voltage: programStatusMessage.data.band_voltage ? [...programStatusMessage.data.band_voltage] : [],
            },
        }
        : null;

    /** 提交编辑时用 ref 保存“最后一次下发/编辑”的 payload，选择频段确认时优先用 ref，避免用旧 props 回滚 */
    const lastProgramStatusResponseRef = useRef<ProgramStatusResponseMessage | null>(null);
    if (programStatusResponseMessage && !lastProgramStatusResponseRef.current) {
        lastProgramStatusResponseRef.current = programStatusResponseMessage;
    }

    // ---------- 状态 ----------
    const [band, setBand] = useState('20m');
    const [isBandModalVisible, setIsBandModalVisible] = useState(false);
    const [selectedBand, setSelectedBand] = useState('20m');
    const [selectedBandIndex, setSelectedBandIndex] = useState(-1);
    const [isEditBandModalVisible, setIsEditBandModalVisible] = useState(false);
    const [isBandVoltageModalVisible, setIsBandVoltageModalVisible] = useState(false);
    const [bandVoltageSelectedIndex, setBandVoltageSelectedIndex] = useState(-1);
    const [editingBandIndex, setEditingBandIndex] = useState(-1);
    const [editingBandName, setEditingBandName] = useState('');
    const [editingBandValue, setEditingBandValue] = useState(0);
    /** 电压/电流/功率/温度 各保留最近 50 个采样点，用于波形条展示 */
    const [sampleBuffers, setSampleBuffers] = useState<{
      voltage: number[];
      current: number[];
      power: number[];
      temperature: number[];
    }>({ voltage: [], current: [], power: [], temperature: [] });

    /** 与页面显示一致：保留一位小数后入缓冲，这样显示值不变时波形为直线 */
    const toDisplayValue = (num: number) => Math.round(num * 10) / 10;
    /** 最新显示值 ref，供定时器按固定间隔写入缓冲，避免 MQTT 高频触发导致卡顿 */
    const latestDisplayValuesRef = useRef({ v: 0, c: 0, p: 0, t: 0 });

    const deviceId = propDeviceId || (location.state as { deviceId?: string })?.deviceId || '1f75d96b40e8ed8e';
    const isManualMode = programStatusMessageData?.freq_mode === 2;
    const filterLabelMap: Record<number, string> = {
        1: 'LPF160m', 2: 'LPF80m', 3: 'LPF40m', 4: 'LPF20m',
        5: 'LPF15m/17m', 6: 'LPF10m', 7: 'LPF6m', 8: 'LPF 4m', 9: 'LPF2m'
    };
    const filterLabel = amplifierData?.filter != null && filterLabelMap[amplifierData.filter]
        ? filterLabelMap[amplifierData.filter]
        : '—';

    /** amplifierData 更新时只写入 ref，不触发 setState。波形区与天线独立，仅按接收到的数据更新，不做任何与天线相关的特殊处理 */
    useEffect(() => {
        if (!amplifierData) return;
        const vRaw = amplifierData.currentVoltage;
        const cRaw = amplifierData.currentCurrent;
        const tRaw = amplifierData.currentTemperature;
        latestDisplayValuesRef.current = {
            v: vRaw !== undefined ? toDisplayValue(vRaw / 100) : 0,
            c: cRaw !== undefined ? toDisplayValue(cRaw / 100) : 0,
            p: (vRaw !== undefined && cRaw !== undefined) ? toDisplayValue((vRaw * cRaw) / 10000) : 0,
            t: tRaw !== undefined ? toDisplayValue(tRaw / 100) : 0,
        };
    }, [amplifierData]);

    /** 按固定间隔（80ms）从 ref 追加到波形缓冲，限制 setState 频率，保证动画流畅 */
    useEffect(() => {
        const interval = setInterval(() => {
            const { v, c, p, t } = latestDisplayValuesRef.current;
            setSampleBuffers((prev) => ({
                voltage: [...prev.voltage, v].slice(-WAVEFORM_SAMPLE_SIZE),
                current: [...prev.current, c].slice(-WAVEFORM_SAMPLE_SIZE),
                power: [...prev.power, p].slice(-WAVEFORM_SAMPLE_SIZE),
                temperature: [...prev.temperature, t].slice(-WAVEFORM_SAMPLE_SIZE),
            }));
        }, 80);
        return () => clearInterval(interval);
    }, []);

    /** 波形 path 字符串缓存，避免无关重渲染时重复计算 */
    const pathVoltage = useMemo(() => samplesToPath(sampleBuffers.voltage), [sampleBuffers.voltage]);
    const pathCurrent = useMemo(() => samplesToPath(sampleBuffers.current), [sampleBuffers.current]);
    const pathPower = useMemo(() => samplesToPath(sampleBuffers.power), [sampleBuffers.power]);
    const pathTemperature = useMemo(() => samplesToPath(sampleBuffers.temperature), [sampleBuffers.temperature]);

    /** 频率显示：无数据时显示「频段未获取」，否则显示 Hz */
    const formatFrequency = (freq?: number) => (freq ? `${freq} Hz` : t('频段未获取'));

    /** 发送模式切换（0 自动 / 1 协议 / 2 手动 / 3 波段电压），仅组包下发 */
    const sendFreqMode = (mode: 0 | 1 | 2 | 3) => {
        if (!programStatusResponseMessage?.data || !deviceId) return;
        const payload = {
            ...programStatusResponseMessage,
            data: { ...programStatusResponseMessage.data, freq_mode: mode },
        };
        amplifierMqttService.sendJsonCommand(deviceId, payload).catch((err) =>
            console.error(`TopBarMiddle: 发送模式指令失败 mode=${mode}`, err)
        );
    };

    return (
        <div style={{ display: 'flex', gap: 0, marginTop: 'var(--spacing-md)', marginLeft: 'var(--spacing-lg)', marginRight: 'var(--spacing-lg)' }}>
            {/* ---------- 频段选择弹窗（16 格，仅展示 freq_band_name；选中后确认可下发） ---------- */}
            {isBandModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '4px',
                        padding: 'var(--spacing-lg)',
                        width: '600px'
                    }}>
                        <h3 style={{ color: 'white', margin: '0 0 var(--spacing-lg) 0' }}>选择频段</h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 'var(--spacing-sm)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            {Array.from({ length: 16 }).map((_, index) => {
                                const bandName = programStatusMessageData?.freq_band_name?.[index];
                                const uniqueKey = `band-${index}-${bandName || 'empty'}`;
                                const isSelected = selectedBand === uniqueKey || selectedBandIndex === index;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: isSelected ? 'green' : '#333',
                                            padding: 'var(--spacing-md)',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            border: '1px solid #555',
                                            width: '120px',
                                            height: '80px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                        onClick={() => {
                                            if (isSelected) {
                                                setEditingBandIndex(index);
                                                setEditingBandName(bandName || '');
                                                setEditingBandValue(programStatusMessageData?.freq_band?.[index] ?? 0);
                                                setIsEditBandModalVisible(true);
                                            } else {
                                                setSelectedBand(uniqueKey);
                                                setSelectedBandIndex(index);
                                            }
                                        }}
                                    >
                                        {bandName ? <span style={{ color: 'white' }}>{bandName}</span> : null}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'flex-end'
                        }}>
                            <div style={{
                                backgroundColor: '#333',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                border: '1px solid #555',
                                cursor: 'pointer'
                            }}
                            onClick={() => setIsBandModalVisible(false)}>
                                <span style={{ color: 'white' }}>取消</span>
                            </div>
                            <div style={{
                                backgroundColor: '#333',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                border: '1px solid #555',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                const match = selectedBand.match(/band-(\d+)-(.*)/);
                                if (match) {
                                    const index = parseInt(match[1], 10);
                                    const source = lastProgramStatusResponseRef.current ?? programStatusResponseMessage;
                                    const bandValue = source?.data?.freq_band?.[index] ?? programStatusMessageData?.freq_band?.[index];
                                    setBand(bandValue != null ? String(bandValue) : '');
                                    setSelectedBandIndex(index);
                                    setIsBandModalVisible(false);
                                    if (source?.data && deviceId) {
                                        const payload = {
                                            ...source,
                                            data: {
                                                ...source.data,
                                                freq_band_name: [...(source.data.freq_band_name || [])],
                                                freq_band: [...(source.data.freq_band || [])]
                                            }
                                        };
                                        while (payload.data.freq_band.length <= index) payload.data.freq_band.push(0);
                                        payload.data.freq_band[index] = bandValue ?? 0;
                                        amplifierMqttService.sendJsonCommand(deviceId, payload).catch((err) =>
                                            console.error('TopBarMiddle: 发送频段选择指令失败', err)
                                        );
                                    }
                                } else {
                                    setBand('');
                                    setIsBandModalVisible(false);
                                }
                            }}>
                                <span style={{ color: 'white' }}>确认</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- 编辑频段弹窗（展示 Message 数据，提交写入 Response 并 sendJsonCommand） ---------- */}
            {isEditBandModalVisible && (

                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                        zIndex: 1001
                    }}
                    onClick={() => setIsEditBandModalVisible(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333',
                            borderRadius: '4px',
                            padding: 'var(--spacing-lg)',
                            width: '400px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ color: 'white', margin: '0 0 var(--spacing-lg) 0' }}>编辑频段</h3>
                        {/* 提交表单时向 MQTT 发送编辑后的 freq_band_name / freq_band */}
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!deviceId || editingBandIndex < 0) return;
                                const applyEdit = () => {
                                    setIsEditBandModalVisible(false);
                                    setSelectedBand(`band-${editingBandIndex}-${editingBandName || 'empty'}`);
                                    setSelectedBandIndex(editingBandIndex);
                                    setBand(String(editingBandValue));
                                };
                                const source = programStatusResponseMessage ?? lastProgramStatusResponseRef.current;
                                if (source?.data) {
                                    const names = [...(source.data.freq_band_name || [])];
                                    const values = [...(source.data.freq_band || [])];
                                    while (names.length <= editingBandIndex) names.push('');
                                    while (values.length <= editingBandIndex) values.push(0);
                                    names[editingBandIndex] = editingBandName;
                                    values[editingBandIndex] = editingBandValue;
                                    const payload: ProgramStatusResponseMessage = {
                                        ...source,
                                        data: {
                                            ...source.data,
                                            freq_band_name: names,
                                            freq_band: values,
                                        },
                                    };
                                    amplifierMqttService
                                        .sendJsonCommand(deviceId, payload)
                                        .then(() => {
                                            lastProgramStatusResponseRef.current = payload;
                                            applyEdit();
                                        })
                                        .catch((err) => {
                                            console.error('TopBarMiddle: 发送频段编辑指令失败', err);
                                        });
                                } else {
                                    console.warn('TopBarMiddle: 无 programStatusMessage，无法发送频段编辑到 MQTT');
                                    applyEdit();
                                }
                            }}
                        >
                            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <span style={{ color: 'white', display: 'block', marginBottom: 'var(--spacing-xs)' }}>频段名称</span>
                                    <input
                                        type="text"
                                        value={editingBandName}
                                        onChange={(e) => setEditingBandName(e.target.value)}
                                        style={{ width: '100%', padding: 'var(--spacing-sm)', backgroundColor: '#333', border: '1px solid #555', color: 'white' }}
                                    />
                                </div>
                                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <span style={{ color: 'white', display: 'block', marginBottom: 'var(--spacing-xs)' }}>频段值</span>
                                    <input
                                        type="number"
                                        value={editingBandValue}
                                        onChange={(e) => setEditingBandValue(Number(e.target.value))}
                                        style={{ width: '100%', padding: 'var(--spacing-sm)', backgroundColor: '#333', border: '1px solid #555', color: 'white' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    style={{ backgroundColor: '#333', padding: 'var(--spacing-sm) var(--spacing-md)', border: '1px solid #555', cursor: 'pointer', color: 'white' }}
                                    onClick={() => setIsEditBandModalVisible(false)}
                                >
                                    取消
                                </button>
                                <button
                                    type="submit"
                                    style={{ backgroundColor: '#333', padding: 'var(--spacing-sm) var(--spacing-md)', border: '1px solid #555', cursor: 'pointer', color: 'white' }}
                                >
                                    确认
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ---------- 波段电压弹窗：从 freq_band_name/freq_band 取格，可将 bandVoltage 绑定到某一波段 ---------- */}
            {isBandVoltageModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: '4px',
                        padding: 'var(--spacing-lg)',
                        width: '600px'
                    }}>
                        <h3 style={{ color: 'white', margin: '0 0 var(--spacing-md) 0' }}>波段电压</h3>
                        <div style={{ marginBottom: 'var(--spacing-md)', color: '#b0b0b0', fontSize: '0.9em' }}>
                            当前波段电压：{amplifierData?.bandVoltage != null ? amplifierData.bandVoltage : '—'}
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 'var(--spacing-sm)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            {Array.from({ length: 16 }).map((_, index) => {
                                const bandName = programStatusMessageData?.freq_band_name?.[index];
                                const boundValue = programStatusMessageData?.band_voltage?.[index];
                                const isBound = boundValue != null && boundValue !== -1;
                                const isSelected = bandVoltageSelectedIndex === index;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: isSelected ? 'green' : '#333',
                                            padding: 'var(--spacing-md)',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            border: '1px solid #555',
                                            width: '120px',
                                            height: '80px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: 4
                                        }}
                                        onClick={() => setBandVoltageSelectedIndex(index)}
                                    >
                                        {bandName ? <span style={{ color: 'white' }}>{bandName}</span> : null}
                                        <span style={{ color: isBound ? '#ffcc00' : '#888', fontSize: '0.8em' }}>
                                            {isBound ? `已绑定: ${boundValue}` : '未绑定'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{
                                backgroundColor: '#333',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                border: '1px solid #555',
                                cursor: 'pointer'
                            }} onClick={() => setIsBandVoltageModalVisible(false)}>
                                <span style={{ color: 'white' }}>关闭</span>
                            </div>
                            <div style={{
                                backgroundColor: bandVoltageSelectedIndex >= 0 ? '#2a7a2a' : '#555',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                border: '1px solid #555',
                                cursor: bandVoltageSelectedIndex >= 0 ? 'pointer' : 'not-allowed',
                                opacity: bandVoltageSelectedIndex >= 0 ? 1 : 0.7
                            }} onClick={() => {
                                if (bandVoltageSelectedIndex < 0 || !deviceId) return;
                                const source = lastProgramStatusResponseRef.current ?? programStatusResponseMessage;
                                if (!source?.data) return;
                                const bandVoltageArray = source.data.band_voltage ? [...source.data.band_voltage] : Array(16).fill(-1);
                                while (bandVoltageArray.length <= bandVoltageSelectedIndex) bandVoltageArray.push(-1);
                                bandVoltageArray[bandVoltageSelectedIndex] = amplifierData?.bandVoltage ?? 0;
                                const payload: ProgramStatusResponseMessage = {
                                    ...source,
                                    data: { ...source.data, band_voltage: bandVoltageArray }
                                };
                                amplifierMqttService.sendJsonCommand(deviceId, payload).catch((err) =>
                                    console.error('TopBarMiddle: 发送波段电压绑定失败', err)
                                );
                            }}>
                                <span style={{ color: 'white' }}>绑定到该波段</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- 左侧面板：频率、模式（自动/协议/手动/波段电压）、滤波器、频段 ---------- */}
            <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRight: 'none',
                padding: 'var(--spacing-lg)',
                maxWidth: '25%',
                flex: '0 0 25%'
            }}>
                <div style={{
                    textAlign: 'center',
                    marginBottom: 'var(--spacing-lg)'
                }}>
                    <h2 style={{
                        color: 'white',
                        fontSize: '2rem',
                        margin: 0
                    }}>{formatFrequency(amplifierData?.frequency)}</h2>
                </div>

                <div style={{ display: 'flex', marginBottom: 'var(--spacing-lg)' }}>
                    <div
                        style={{
                            flex: 1,
                            backgroundColor: programStatusMessageData?.freq_mode === 0 ? 'green' : '#333',
                            padding: 'var(--spacing-sm)',
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            border: '1px solid #555'
                        }}
                        onClick={() => sendFreqMode(0)}
                    >
                        <span style={{ color: 'white' }}>自动模式</span>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            backgroundColor: programStatusMessageData?.freq_mode === 1 ? 'green' : '#333',
                            padding: 'var(--spacing-sm)',
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            border: '1px solid #555'
                        }}
                        onClick={() => {
                            if (programStatusMessageData?.freq_mode !== 1) {
                                sendFreqMode(1);
                            }
                        }}
                    >
                        <span style={{ color: 'white' }}>协议模式</span>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            backgroundColor: isManualMode ? 'green' : '#333',
                            padding: 'var(--spacing-sm)',
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            border: '1px solid #555'
                        }}
                        onClick={() => sendFreqMode(2)}
                    >
                        <span style={{ color: 'white' }}>{t('手动选择')}</span>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            backgroundColor: Number(programStatusMessageData?.freq_mode) === 3 ? 'green' : '#333',
                            padding: 'var(--spacing-sm)',
                            textAlign: 'center',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            border: '1px solid #555'
                        }}
                        onClick={() => {
                            if (Number(programStatusMessageData?.freq_mode) === 3) {
                                setBandVoltageSelectedIndex(-1);
                                setIsBandVoltageModalVisible(true);
                            } else {
                                sendFreqMode(3);
                            }
                        }}
                    >
                        <span style={{ color: 'white' }}>波段电压</span>
                        {amplifierData?.bandVoltage != null && (
                            <div style={{ color: '#ffcc00', fontSize: '0.85em', marginTop: 2 }}>
                                {amplifierData.bandVoltage}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <span style={{ color: 'white' }}>{t('滤波器：')}</span>
                        <span style={{ color: '#ffcc00' }}>{filterLabel}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <span style={{ color: 'white' }}>{t('频段：')}</span>
                        <div style={{
                            backgroundColor: isManualMode ? '#333' : '#222',
                            padding: 'var(--spacing-sm) var(--spacing-md)',
                            border: '1px solid #555',
                            cursor: isManualMode ? 'pointer' : 'not-allowed',
                            width: '80px',
                            textAlign: 'center',
                            opacity: isManualMode ? 1 : 0.6
                        }}
                        onClick={() => {
                            if (!isManualMode) return;
                            setIsBandModalVisible(true);
                        }}>
                            <span style={{ color: 'white' }}>{band}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                padding: 'var(--spacing-lg)',
                flex: '0 0 50%'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginBottom: 'var(--spacing-md)'
                }}>
                    <h3 style={{ color: 'white', margin: 0 }}>{t('输入')}</h3>
                    <h3 style={{ color: 'white', margin: 0 }}>{t('输出')}</h3>
                </div>

                <div style={{ display: 'flex', gap: 'var(--spacing-lg)' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{
                            backgroundColor: '#333',
                            padding: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-md)',
                            textAlign: 'center'
                        }}>
                            <span style={{ color: 'white', fontSize: '1.5em' }}>{amplifierData?.inputPowerPositive !== undefined ? `${amplifierData.inputPowerPositive.toFixed(1)} W` : '--'}</span>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div style={{
                                flex: 1,
                                backgroundColor: '#333',
                                padding: 'var(--spacing-md)',
                                textAlign: 'center'
                            }}>
                                <div style={{ color: '#999', fontSize: '0.8em', marginBottom: 'var(--spacing-xs)' }}>{t('反向功率')}</div>
                                <span style={{ color: 'white' }}>{amplifierData?.inputPowerNegative !== undefined ? `${amplifierData.inputPowerNegative.toFixed(1)} W` : '--'}</span>
                            </div>
                            <div style={{
                                flex: 1,
                                backgroundColor: '#333',
                                padding: 'var(--spacing-md)',
                                textAlign: 'center'
                            }}>
                                <div style={{ color: '#999', fontSize: '0.8em', marginBottom: 'var(--spacing-xs)' }}>{t('驻波')}</div>
                                <span style={{ color: 'white' }}>{amplifierData?.inputRf !== undefined ? (amplifierData.inputRf / 10).toFixed(1) : '--'}</span>
                            </div>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                            <span style={{ color: '#999', fontSize: '0.8em' }}>{t('驻波')}</span>
                        </div>
                        <div style={{ display: 'flex', height: '10px', gap: '2px', position: 'relative' }}>
                            {/* 驻波=0 或无数据：整条灰色；有数据时再显示绿/黄/红与指示点（展示与动画均用 原始值/10） */}
                            {(amplifierData?.inputRf == null || amplifierData.inputRf <= 0) ? (
                                <div style={{ flex: 1, backgroundColor: '#444', borderRadius: '2px' }} />
                            ) : (
                                <>
                                    <div style={{ flex: '0 0 10%', backgroundColor: 'green' }} />
                                    <div style={{
                                        flex: `0 0 ${(amplifierData.inputRf / 10) > 1 ? Math.min((((amplifierData.inputRf / 10) - 1) / 2) * 60, 60) : 0}%`,
                                        backgroundColor: 'yellow'
                                    }} />
                                    <div style={{
                                        flex: `0 0 ${(amplifierData.inputRf / 10) > 3 ? Math.min((((amplifierData.inputRf / 10) - 3) / 21) * 30, 30) : 0}%`,
                                        backgroundColor: 'red'
                                    }} />
                                    <div style={{ flex: 1, backgroundColor: '#444', minWidth: 0 }} />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: `${Math.min(((amplifierData.inputRf / 10) / 24) * 100, 100)}%`,
                                            top: '-50%',
                                            width: '4px',
                                            height: '20px',
                                            backgroundColor: '#ffffff',
                                            transform: 'translateX(-50%)',
                                            transition: 'left 0.2s ease-out'
                                        }}
                                    />
                                </>
                            )}
                            {/* 刻度标记始终显示 */}
                            <div style={{ position: 'absolute', left: '10%', top: '100%', marginTop: '2px', fontSize: '0.7em', color: '#999' }}>1</div>
                            <div style={{ position: 'absolute', left: '70%', top: '100%', marginTop: '2px', fontSize: '0.7em', color: '#999' }}>3</div>
                            <div style={{ position: 'absolute', left: '98%', top: '100%', marginTop: '2px', fontSize: '0.7em', color: '#999' }}>24</div>
                        </div>
                    </div>

                    <div style={{ flex: 1 }}>
                        <div style={{
                            backgroundColor: '#333',
                            padding: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-md)',
                            textAlign: 'center'
                        }}>
                            <span style={{ color: 'white', fontSize: '1.5em' }}>{amplifierData?.outputPowerPositive !== undefined ? `${amplifierData.outputPowerPositive.toFixed(1)} W` : '--'}</span>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div style={{
                                flex: 1,
                                backgroundColor: '#333',
                                padding: 'var(--spacing-md)',
                                textAlign: 'center'
                            }}>
                                <div style={{ color: '#999', fontSize: '0.8em', marginBottom: 'var(--spacing-xs)' }}>{t('反向功率')}</div>
                                <span style={{ color: 'white' }}>{amplifierData?.outputPowerNegative !== undefined ? `${amplifierData.outputPowerNegative.toFixed(1)} W` : '--'}</span>
                            </div>
                            <div style={{
                                flex: 1,
                                backgroundColor: '#333',
                                padding: 'var(--spacing-md)',
                                textAlign: 'center'
                            }}>
                                <div style={{ color: '#999', fontSize: '0.8em', marginBottom: 'var(--spacing-xs)' }}>{t('驻波')}</div>
                                <span style={{ color: 'white' }}>{amplifierData?.outputRf !== undefined ? (amplifierData.outputRf / 10).toFixed(1) : '--' }</span>
                            </div>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                            <span style={{ color: '#999', fontSize: '0.8em' }}>{t('驻波')}</span>
                        </div>
                        <div style={{ display: 'flex', height: '10px', gap: '2px', position: 'relative' }}>
                            {/* 驻波=0 或无数据：整条灰色；有数据时再显示绿/黄/红与指示点（展示与动画均用 原始值/10） */}
                            {(amplifierData?.outputRf == null || amplifierData.outputRf <= 0) ? (
                                <div style={{ flex: 1, backgroundColor: '#444', borderRadius: '2px' }} />
                            ) : (
                                <>
                                    <div style={{ flex: '0 0 10%', backgroundColor: 'green' }} />
                                    <div style={{
                                        flex: `0 0 ${(amplifierData.outputRf / 10) > 1 ? Math.min((((amplifierData.outputRf / 10) - 1) / 2) * 60, 60) : 0}%`,
                                        backgroundColor: 'yellow'
                                    }} />
                                    <div style={{
                                        flex: `0 0 ${(amplifierData.outputRf / 10) > 3 ? Math.min((((amplifierData.outputRf / 10) - 3) / 21) * 30, 30) : 0}%`,
                                        backgroundColor: 'red'
                                    }} />
                                    <div style={{ flex: 1, backgroundColor: '#444', minWidth: 0 }} />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: `${Math.min(((amplifierData.outputRf / 10) / 24) * 100, 100)}%`,
                                            top: '-50%',
                                            width: '4px',
                                            height: '20px',
                                            backgroundColor: '#ffffff',
                                            transform: 'translateX(-50%)',
                                            transition: 'left 0.2s ease-out'
                                        }}
                                    />
                                </>
                            )}
                            {/* 刻度标记始终显示 */}
                            <div style={{ position: 'absolute', left: '10%', top: '100%', marginTop: '2px', fontSize: '0.7em', color: '#999' }}>1</div>
                            <div style={{ position: 'absolute', left: '70%', top: '100%', marginTop: '2px', fontSize: '0.7em', color: '#999' }}>3</div>
                            <div style={{ position: 'absolute', left: '98%', top: '100%', marginTop: '2px', fontSize: '0.7em', color: '#999' }}>24</div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                padding: 'var(--spacing-lg)',
                flex: '0 0 25%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'white' }}>{t('电压:')}</span>
                        <span style={{ color: 'white' }}>{amplifierData?.currentVoltage !== undefined ? `${(amplifierData.currentVoltage / 100).toFixed(1)} V` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathVoltage}
                                stroke="#ffcc00"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
                <div style={{ height: '1px', backgroundColor: '#333' }}></div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'white' }}>{t('电流:')}</span>
                        <span style={{ color: 'white' }}>{amplifierData?.currentCurrent !== undefined ? `${(amplifierData.currentCurrent/100).toFixed(1)} A` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathCurrent}
                                stroke="#ffcc00"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
                <div style={{ height: '2px', backgroundColor: '#333' }}></div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'white' }}>{t('功率:')}</span>
                        <span style={{ color: 'white' }}>{amplifierData?.currentVoltage !== undefined && amplifierData?.currentCurrent !== undefined ? `${((amplifierData.currentVoltage * amplifierData.currentCurrent)/10000).toFixed(1)} W` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathPower}
                                stroke="#ffcc00"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
                <div style={{ height: '1px', backgroundColor: '#333' }}></div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'white' }}>{t('温度:')}</span>
                        <span style={{ color: 'white' }}>{amplifierData?.currentTemperature !== undefined ? `${(amplifierData.currentTemperature / 100).toFixed(1)} °C` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathTemperature}
                                stroke="#ffcc00"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

/** 使用 memo 避免父组件因天线等无关 state 更新时重渲染，防止“整页刷新”感 */
export const TopBarMiddle = React.memo(TopBarMiddleInner);
export default TopBarMiddle;